import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  fetchInbox,
  openTestDrafts,
  setTestPerformer,
  type Inbox,
  type InboxRow,
  type InboxTray,
  type InboxType,
  type TestPerformer,
} from '../api/tests';
import { ca } from '../i18n/ca';
import { idOf, isCard, rowReason, rowState, rowTitle } from './inboxRows';
import { errorText } from './useSessionLive';
import { fmtDay } from './dates';

const TRAYS: InboxTray[] = ['todo', 'review', 'done', 'skip'];
const TYPES: InboxType[] = ['play', 'lend', 'judge', 'campaign', 'captures', 'smoke'];
const TRAY_ICON: Record<InboxTray, string> = {
  todo: 'bi-flag',
  review: 'bi-eye',
  done: 'bi-check2-circle',
  skip: 'bi-archive',
};
const TYPE_ICON: Record<InboxType, string> = {
  play: 'bi-controller',
  lend: 'bi-pc-display',
  judge: 'bi-question-circle',
  campaign: 'bi-images',
  captures: 'bi-camera',
  smoke: 'bi-moon-stars',
};
/** «Web» is no type of its own: it is the filter of the cards answered in the browser. */
const WEB_ICON = 'bi-globe2';

/** A card answered in the browser; every other card is played in the game. */
const isWebRow = (row: InboxRow) => row.medium === 'web';
/** The kind a row is shown as: a web card reads «Web» and carries the globe, a game card «Joc» and the controller. */
const rowIcon = (row: InboxRow) => (isWebRow(row) ? WEB_ICON : TYPE_ICON[row.type]);
type Filter = InboxType | 'web';

const day = (iso: string | null) => (iso ? fmtDay(iso) : '');

function stateClass(row: InboxRow): string {
  if (row.type === 'smoke') return row.state === 'green' ? 'ok' : row.state === 'red' ? 'bad' : '';
  if (row.tray === 'done') return 'ok';
  if (row.tray === 'review') return 'warn';
  if (row.tray === 'todo' && row.state === 'open') return 'ok';
  return '';
}

function action(row: InboxRow): string {
  const a = ca.inbox.actions;
  if (row.tray === 'todo') {
    if (row.state !== 'open') return a.open;
    return row.type === 'judge' ? a.answer : a.take;
  }
  if (row.tray === 'review') return row.state === 'in_progress' ? a.resume : a.review;
  if (row.tray === 'skip' && (row.reason === 'superseded' || row.reason === 'old')) {
    return a.reviewAnyway;
  }
  return a.open;
}

/** The facts under a row's name, in the order a person asks them: what, which version, how much, who. */
function meta(row: InboxRow, isAdmin: boolean): string[] {
  const t = ca.inbox;
  const out: string[] = [isWebRow(row) ? t.types.web : (t.types[row.type] ?? row.type)];
  if (row.version) out.push(t.version(row.version));
  if (row.type === 'campaign' && row.tours != null) {
    out.push(t.tours(row.tours, row.toursToReview ?? 0));
  }
  if (row.total != null && row.reviewed != null) {
    if (row.type === 'play') {
      out.push(t.steps(row.reviewed, row.total));
      if ((row.problems ?? 0) > 0) out.push(t.noAnswers(row.problems ?? 0));
    } else {
      out.push(t.pictures(row.total));
      if (row.reviewed > 0) out.push(t.reviewed(row.reviewed, row.total));
      if ((row.problems ?? 0) > 0) out.push(t.problems(row.problems ?? 0));
    }
  }
  // A session's code is the organiser's handle on it; a tester reads the card's own title.
  if (isAdmin && isCard(row) && row.sessionCode) out.push(t.session(row.sessionCode));
  if (row.who) out.push(t.by(row.who));
  if (row.date) out.push(day(row.date));
  return out;
}

/**
 * Every test and every review in one list ({@code GET /api/tests/inbox}; the
 * owner, 2026-09-29). Four trays with their counts are the page's first
 * question (what waits for me, what is done, what needs no look), the type is
 * the second, and every row has the same shape: what it is, what it ran on,
 * how far it is, where it stands, and one button.
 *
 * An admin gets all of it. Anybody else gets the cards they took, under the
 * same four trays, and nothing of the platform's own work.
 */
export function ProvesInbox({
  isAdmin,
  onError,
}: {
  isAdmin: boolean;
  onError: (e: string | null) => void;
}) {
  const t = ca.inbox;
  const [params, setParams] = useSearchParams();
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    fetchInbox()
      .then((next) => {
        setInbox(next);
        onError(null);
      })
      .catch((e) => onError(errorText(e)));
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  const asked = params.get('safata') as InboxTray | null;
  // The page opens on «Per fer» for everybody (the owner, 2026-10-13); a tray named in
  // the address still wins, and nothing is remembered between visits.
  const tray: InboxTray = asked && TRAYS.includes(asked) ? asked : 'todo';
  // «web» is no type of its own: a web card is a play card answered in the browser, and «Joc» keeps the others.
  const type = (params.get('tipus') ?? '') as Filter | '';
  const q = (params.get('q') ?? '').trim().toLowerCase();

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const inTray = useMemo(() => (inbox?.rows ?? []).filter((r) => r.tray === tray), [inbox, tray]);
  const filters = useMemo(
    () =>
      TYPES.flatMap((k): Filter[] => {
        if (k !== 'play') return inTray.some((r) => r.type === k) ? [k] : [];
        return [
          ...(inTray.some((r) => r.type === 'play' && !isWebRow(r)) ? (['play'] as Filter[]) : []),
          ...(inTray.some(isWebRow) ? (['web'] as Filter[]) : []),
        ];
      }),
    [inTray],
  );
  const drafts = useMemo(() => inTray.filter((r) => isCard(r) && r.state === 'draft'), [inTray]);
  const rows = useMemo(
    () =>
      inTray.filter((r) => {
        if (
          type === 'web'
            ? !isWebRow(r)
            : type === 'play'
              ? r.type !== 'play' || isWebRow(r)
              : type && r.type !== type
        ) {
          return false;
        }
        if (!q) return true;
        const hay =
          `${rowTitle(r, isAdmin)} ${r.sessionCode ?? ''} ${r.version ?? ''} ${r.who ?? ''}`.toLowerCase();
        return hay.includes(q);
      }),
    [inTray, type, q, isAdmin],
  );
  const pickedRows = rows.filter((r) => picked.has(r.key));

  const mark = (performer: TestPerformer) => {
    const ids = pickedRows.map((r) => Number(idOf(r)));
    if (ids.length === 0) return;
    setTestPerformer(ids, performer)
      .then(() => {
        setPicked(new Set());
        setPicking(false);
        load();
      })
      .catch((e) => onError(errorText(e)));
  };

  // «Obre-les totes»: an imported batch, read and found good, is offered and announced in one go.
  const openAll = () => {
    const ids = drafts.map((r) => Number(idOf(r)));
    if (ids.length === 0 || !window.confirm(t.openAllConfirm(ids.length))) return;
    openTestDrafts(ids)
      .then(() => load())
      .catch((e) => onError(errorText(e)));
  };

  return (
    <section className="inbox" aria-label={isAdmin ? t.title : t.mineTitle}>
      {!isAdmin && <p className="muted">{inbox?.tester ? t.testerIntro : t.mineIntro}</p>}
      <div className="inbox-trays" role="group" aria-label={isAdmin ? t.title : t.mineTitle}>
        {TRAYS.map((name) => (
          <button
            key={name}
            type="button"
            className={`inbox-tray${tray === name ? ' on' : ''}`}
            aria-pressed={tray === name}
            title={t.trayHints[name]}
            onClick={() => {
              const next = new URLSearchParams(params);
              next.set('safata', name);
              next.delete('tipus');
              setParams(next, { replace: true });
              setPicked(new Set());
            }}
          >
            <span className="label">
              <i className={`bi ${TRAY_ICON[name]}`} aria-hidden="true" /> {t.trays[name]}
            </span>
            <span className="count num">{inbox ? inbox.counts[name] : '·'}</span>
          </button>
        ))}
      </div>
      <p className="muted small">
        {isAdmin || tray !== 'todo'
          ? t.trayHints[tray]
          : inbox?.tester
            ? t.trayHints.todoTester
            : t.trayHints.todoMine}
      </p>

      {inTray.length > 0 && (
        <div className="toolbar inbox-filters" role="group" aria-label={t.typeFilter}>
          {filters.length > 1 && (
            <>
              <button
                type="button"
                className={`chip toggle ${type === '' ? 'on' : ''}`}
                aria-pressed={type === ''}
                onClick={() => set('tipus', '')}
              >
                {t.types.all}
              </button>
              {filters.map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`chip toggle ${type === k ? 'on' : ''}`}
                  aria-pressed={type === k}
                  onClick={() => set('tipus', type === k ? '' : k)}
                >
                  <i className={`bi ${k === 'web' ? WEB_ICON : TYPE_ICON[k]}`} aria-hidden="true" />
                  {t.types[k]}
                </button>
              ))}
            </>
          )}
          {isAdmin && drafts.length > 0 && (
            <button type="button" className="chip toggle" onClick={openAll}>
              <i className="bi bi-megaphone" aria-hidden="true" />
              {t.openAllDrafts(drafts.length)}
            </button>
          )}
          {isAdmin && inTray.some(isCard) && (
            <button
              type="button"
              className={`chip toggle ${picking ? 'on' : ''}`}
              aria-pressed={picking}
              onClick={() => {
                setPicking(!picking);
                setPicked(new Set());
              }}
            >
              <i className="bi bi-check2-square" aria-hidden="true" />
              {picking ? t.cancelSelect : t.select}
            </button>
          )}
          <span className="grow" />
          <input
            type="search"
            className="control"
            aria-label={t.search}
            placeholder={t.searchPlaceholder}
            value={params.get('q') ?? ''}
            onChange={(e) => set('q', e.target.value)}
          />
        </div>
      )}

      {picking && pickedRows.length > 0 && (
        <p className="row actions" role="status">
          <span>{t.selected(pickedRows.length)}</span>
          <button type="button" onClick={() => mark('agent')}>
            <i className="bi bi-robot" aria-hidden="true" /> {t.markAgent}
          </button>
          <button type="button" onClick={() => mark('person')}>
            <i className="bi bi-person" aria-hidden="true" /> {t.markPerson}
          </button>
        </p>
      )}

      {inbox && rows.length === 0 && (
        <p className="muted">{!isAdmin && inbox.rows.length === 0 ? t.mineNone : t.empty[tray]}</p>
      )}
      <ul className="inbox-rows">
        {rows.map((row) => {
          const reason = rowReason(row, isAdmin);
          const counts = row.total != null && row.reviewed != null && row.total > 0;
          return (
            <li
              key={row.key}
              className={`inbox-row tray-${row.tray} type-${isWebRow(row) ? 'web' : row.type}`}
            >
              {picking && isCard(row) ? (
                <input
                  type="checkbox"
                  aria-label={rowTitle(row, isAdmin)}
                  checked={picked.has(row.key)}
                  onChange={() =>
                    setPicked((old) => {
                      const next = new Set(old);
                      if (next.has(row.key)) next.delete(row.key);
                      else next.add(row.key);
                      return next;
                    })
                  }
                />
              ) : (
                <i className={`bi ${rowIcon(row)} inbox-icon`} aria-hidden="true" />
              )}
              <div className="inbox-body">
                <Link to={row.link} className="inbox-title">
                  {rowTitle(row, isAdmin)}
                </Link>
                {isCard(row) && row.performer && (
                  <span className={`chip small performer-${row.performer}`}>
                    <i
                      className={`bi ${row.performer === 'agent' ? 'bi-robot' : 'bi-person'}`}
                      aria-hidden="true"
                    />
                    {t.performers[row.performer]}
                  </span>
                )}
                <p className="muted small inbox-meta">{meta(row, isAdmin).join(' · ')}</p>
                {counts && (
                  <progress
                    className="inbox-progress"
                    max={row.total ?? 1}
                    value={row.reviewed ?? 0}
                    aria-label={
                      row.type === 'play'
                        ? t.steps(row.reviewed ?? 0, row.total ?? 0)
                        : t.reviewed(row.reviewed ?? 0, row.total ?? 0)
                    }
                  />
                )}
                {reason && <p className="muted small inbox-reason">{reason}</p>}
                {row.tray === 'done' && row.note && <p className="small">{row.note}</p>}
              </div>
              {row.previews.length > 0 && (
                <Link to={row.link} className="inbox-previews" tabIndex={-1} aria-hidden="true">
                  {row.previews.slice(0, 3).map((url) => (
                    <img key={url} src={url} alt="" loading="lazy" />
                  ))}
                </Link>
              )}
              <div className="inbox-end">
                <span className={`chip ${stateClass(row)}`}>{rowState(row)}</span>
                <Link
                  className={`button${row.tray === 'review' || (row.tray === 'todo' && row.state === 'open') ? ' primary' : ''}`}
                  to={row.link}
                >
                  {action(row)}
                </Link>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
